'use client';

import { Skeleton } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useContextTabBack } from '@/features/context-tabs';
import {
  useApplyJobChangeOrderMutation,
  useCreateJobChangeOrderItemMutation,
  useDeleteJobChangeOrderItemMutation,
  useGetJobChangeOrderQuery,
  useGetJobDeliverablesQuery,
  usePatchJobChangeOrderMutation,
} from '@/features/jobs/api/jobsApi';
import { useGetServiceTypesQuery } from '@/features/service-types/api/service-typesApi';
import {
  ServiceTypeModalForm,
  ServiceTypeSelect,
} from '@/features/service-types/components';
import {
  useCanScopedWorkspaceAction,
  useCanWorkspaceAction,
} from '@/features/workspaces/hooks';
import { useWorkspaceRoutes } from '@/hooks/useWorkspaceRoutes';
import {
  BackButton,
  Badge,
  ConfirmActionModal,
  DataBadge,
  DeleteIcon,
  EmptyState,
  MainButton,
  Modal,
  OptionsMenu,
  PlusIcon,
  SectionStepperTabs,
  SecondaryButton,
  SmartInput,
} from '@web/ui';
import { getErrorMessage, notify } from '@web/utils';

import styles from './JobChangeOrderDetailView.module.scss';
import { formatImpactHours } from './utils';

import type {
  ChangeOrderItemType,
  CreateJobChangeOrderItemPayload,
  JobChangeOrderDetail,
  JobChangeOrderItem,
  JobDeliverable,
  JobDeliverableServiceTypePool,
} from '../../types';
import type { ServiceType } from '@web/types';

type StepId = 'summary' | 'changes' | 'netnet' | 'apply';

type JobChangeOrderDetailViewProps = {
  jobId: string;
  changeOrderId: string;
  jobName: string;
  jobServiceTypeIds: string[];
};

type ServiceTypeOption = {
  id: string;
  label: string;
};

type SummaryDraft = {
  name: string;
  notes: string;
};

const STEPS: Array<{ id: StepId; label: string }> = [
  { id: 'summary', label: '1. Summary' },
  { id: 'changes', label: '2. Changes' },
  { id: 'netnet', label: '3. Net Net' },
  { id: 'apply', label: '4. Apply' },
];

function normalizeDeliverables(raw: unknown): JobDeliverable[] {
  if (Array.isArray(raw)) {
    return raw as JobDeliverable[];
  }

  if (raw && typeof raw === 'object') {
    const candidate = raw as { results?: unknown };
    if (Array.isArray(candidate.results)) {
      return candidate.results as JobDeliverable[];
    }
  }

  return [];
}

function hasPositiveDelta(deltas: Record<string, number>) {
  return Object.values(deltas).some((value) => Number(value) > 0);
}

function toPositiveDeltas(deltas: Record<string, number>) {
  return Object.fromEntries(
    Object.entries(deltas).filter(([, value]) => Number(value) > 0),
  );
}

function getItemTitle(
  item: JobChangeOrderItem,
  deliverablesById: Map<string, JobDeliverable>,
) {
  if (item.item_type === 'add_new_deliverable') {
    return `New Deliverable: ${item.payload?.name || 'Untitled deliverable'}`;
  }

  const deliverableName = item.deliverable_id
    ? deliverablesById.get(item.deliverable_id)?.name
    : null;

  return `Existing Deliverable: ${deliverableName || 'Selected deliverable'}`;
}

function getServiceTypeOptions(
  serviceTypes: ServiceType[],
  deliverables: JobDeliverable[],
): ServiceTypeOption[] {
  const options = new Map<string, ServiceTypeOption>();

  serviceTypes.forEach((serviceType) => {
    options.set(serviceType.id, {
      id: serviceType.id,
      label: serviceType.label,
    });
  });

  deliverables.forEach((deliverable) => {
    deliverable.service_type_pools.forEach((pool) => {
      const id = getPoolServiceTypeId(pool);
      if (!options.has(id)) {
        options.set(id, { id, label: pool.service_type.label });
      }
    });
  });

  return [...options.values()];
}

function getJobServiceTypeOptions(
  serviceTypes: ServiceTypeOption[],
  jobServiceTypeIds: string[],
  deliverables: JobDeliverable[],
) {
  const ids =
    jobServiceTypeIds.length > 0
      ? new Set(jobServiceTypeIds)
      : new Set(
          deliverables.flatMap((deliverable) =>
            deliverable.service_type_pools.map(getPoolServiceTypeId),
          ),
        );

  return serviceTypes.filter((serviceType) => ids.has(serviceType.id));
}

function getPoolServiceTypeId(pool: JobDeliverableServiceTypePool) {
  return pool.service_type_id ?? pool.service_type.id;
}

function StepHeading({
  stepIndex,
  title,
  description,
}: {
  stepIndex: number;
  title: string;
  description: string;
}) {
  return (
    <>
      <span>
        <Badge label={`Step ${stepIndex} of 4`} tone="default" />
      </span>
      <h2 className={styles.stepTitle}>{title}</h2>
      <p className={styles.stepDescription}>{description}</p>
    </>
  );
}

function ChangeOrderFooter({
  impactHours,
  onCancel,
  onBack,
  onSave,
  onContinue,
  canContinue = true,
  saveLabel = 'Save Draft',
  continueLabel = 'Continue',
  isSaving = false,
}: {
  impactHours: number;
  onCancel: () => void;
  onBack?: () => void;
  onSave?: () => void;
  onContinue: () => void;
  canContinue?: boolean;
  saveLabel?: string;
  continueLabel?: string;
  isSaving?: boolean;
}) {
  return (
    <footer className={styles.footer}>
      <span>Total Impact {formatImpactHours(impactHours)}</span>
      <div className={styles.footer__actions}>
        <SecondaryButton ariaLabel="Cancel change order" onClick={onCancel}>
          Cancel
        </SecondaryButton>
        {onBack ? (
          <SecondaryButton ariaLabel="Back" onClick={onBack}>
            Back
          </SecondaryButton>
        ) : null}
        {onSave ? (
          <MainButton
            ariaLabel={saveLabel}
            onClick={onSave}
            disabled={isSaving}
            isLoading={isSaving}
          >
            {saveLabel}
          </MainButton>
        ) : null}
        <SecondaryButton
          ariaLabel={continueLabel}
          onClick={onContinue}
          disabled={!canContinue || isSaving}
        >
          {continueLabel}
        </SecondaryButton>
      </div>
    </footer>
  );
}

function SummaryStep({
  changeOrder,
  canEdit,
  isSaving,
  onSave,
  onDraftChange,
  onCancel,
  onContinue,
}: {
  changeOrder: JobChangeOrderDetail;
  canEdit: boolean;
  isSaving: boolean;
  onSave: (payload: { name: string; notes: string }) => Promise<boolean>;
  onDraftChange: (draft: SummaryDraft) => void;
  onCancel: () => void;
  onContinue: () => void;
}) {
  const [name, setName] = useState(changeOrder.name);
  const [notes, setNotes] = useState(changeOrder.notes ?? '');

  const save = async ({ notifyOnSuccess = true } = {}) => {
    if (!canEdit) {
      return name.trim().length > 0;
    }

    const ok = await onSave({ name: name.trim(), notes });

    if (ok && notifyOnSuccess) {
      notify({ type: 'success', message: 'Change order saved.' });
    }

    return ok;
  };

  return (
    <div className={styles.stepBody}>
      <StepHeading
        stepIndex={1}
        title="Summarize the change"
        description="Name this change order and capture any helpful context."
      />
      <div className={styles.formGrid}>
        <SmartInput
          type="text"
          name="change-order-name"
          label="Name"
          value={name}
          readOnly={!canEdit}
          onChange={(value) => {
            const nextName = String(value);
            setName(nextName);
            onDraftChange({ name: nextName, notes });
          }}
        />
        <SmartInput
          type="textarea"
          name="change-order-notes"
          label="Notes"
          value={notes}
          readOnly={!canEdit}
          onChange={(value) => {
            const nextNotes = String(value);
            setNotes(nextNotes);
            onDraftChange({ name, notes: nextNotes });
          }}
        />
      </div>
      <ChangeOrderFooter
        impactHours={changeOrder.impact_hours}
        onCancel={onCancel}
        onSave={canEdit ? () => void save() : undefined}
        onContinue={() => {
          void save({ notifyOnSuccess: canEdit }).then((ok) => {
            if (ok) {
              onContinue();
            }
          });
        }}
        canContinue={name.trim().length > 0}
        isSaving={isSaving}
      />
    </div>
  );
}

function DeltaRows({
  serviceTypes,
  availableServiceTypes,
  jobServiceTypeIds,
  deltas,
  onChange,
  onCreateServiceType,
  disabled,
}: {
  serviceTypes: ServiceTypeOption[];
  availableServiceTypes: ServiceTypeOption[];
  jobServiceTypeIds: string[];
  deltas: Record<string, number>;
  onChange: (next: Record<string, number>) => void;
  onCreateServiceType: () => void;
  disabled: boolean;
}) {
  const [serviceTypeToAdd, setServiceTypeToAdd] = useState('');
  const baseIds = new Set(serviceTypes.map((serviceType) => serviceType.id));
  const jobIds = new Set(jobServiceTypeIds);
  const optionsById = new Map(
    availableServiceTypes.map((serviceType) => [serviceType.id, serviceType]),
  );
  const displayedServiceTypes = [
    ...serviceTypes,
    ...Object.keys(deltas)
      .filter((id) => !baseIds.has(id))
      .map((id) => optionsById.get(id))
      .filter((serviceType): serviceType is ServiceTypeOption =>
        Boolean(serviceType),
      ),
  ];
  const displayedIds = displayedServiceTypes.map(
    (serviceType) => serviceType.id,
  );

  const addServiceType = (serviceTypeId: string) => {
    if (!serviceTypeId || displayedIds.includes(serviceTypeId)) {
      return;
    }

    onChange({ ...deltas, [serviceTypeId]: 0 });
    setServiceTypeToAdd('');
  };

  const removeServiceType = (serviceTypeId: string) => {
    const next = { ...deltas };
    delete next[serviceTypeId];
    onChange(next);
  };

  return (
    <div className={styles.serviceTypeEditor}>
      {displayedServiceTypes.length > 0 ? (
        <div className={styles.serviceTypeRows}>
          {displayedServiceTypes.map((serviceType) => (
            <div className={styles.serviceTypeRow} key={serviceType.id}>
              <span className={styles.serviceTypeName}>
                <span>{serviceType.label}</span>
                {!jobIds.has(serviceType.id) ? (
                  <Badge label="New to job" tone="info" />
                ) : null}
              </span>
              <SmartInput
                type="number"
                name={`service-delta-${serviceType.id}`}
                label="Hours"
                value={deltas[serviceType.id] ?? 0}
                readOnly={disabled}
                onChange={(value) =>
                  onChange({
                    ...deltas,
                    [serviceType.id]: Number(value) || 0,
                  })
                }
              />
              {!disabled && !baseIds.has(serviceType.id) ? (
                <SecondaryButton
                  ariaLabel={`Remove ${serviceType.label}`}
                  variant="mini"
                  onClick={() => removeServiceType(serviceType.id)}
                >
                  <DeleteIcon size={16} />
                </SecondaryButton>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          title="No service types selected."
          description="Select an existing service type or create a new one."
        />
      )}

      {!disabled ? (
        <div className={styles.serviceTypeActions}>
          <ServiceTypeSelect
            value={serviceTypeToAdd}
            onChange={addServiceType}
            name="change-order-add-service-type"
            label="Add service type"
            includeAllOption={false}
            excludeIds={displayedIds}
          />
          <SecondaryButton
            ariaLabel="Create Service Type"
            onClick={onCreateServiceType}
          >
            <PlusIcon size={14} />
            &nbsp;Create Service Type
          </SecondaryButton>
        </div>
      ) : null}
    </div>
  );
}

function ChangesStep({
  changeOrder,
  deliverables,
  serviceTypes,
  jobServiceTypes,
  jobServiceTypeIds,
  canEdit,
  isCreatingItem,
  isDeletingItem,
  onCreateItem,
  onDeleteItem,
  onCreateServiceType,
  onCancel,
  onBack,
  onContinue,
}: {
  changeOrder: JobChangeOrderDetail;
  deliverables: JobDeliverable[];
  serviceTypes: ServiceTypeOption[];
  jobServiceTypes: ServiceTypeOption[];
  jobServiceTypeIds: string[];
  canEdit: boolean;
  isCreatingItem: boolean;
  isDeletingItem: boolean;
  onCreateItem: (payload: CreateJobChangeOrderItemPayload) => Promise<boolean>;
  onDeleteItem: (itemId: string) => Promise<void>;
  onCreateServiceType: () => void;
  onCancel: () => void;
  onBack: () => void;
  onContinue: () => void;
}) {
  const [mode, setMode] = useState<Extract<
    ChangeOrderItemType,
    'add_to_existing' | 'add_new_deliverable'
  > | null>(null);
  const [deliverableId, setDeliverableId] = useState('');
  const [existingDeltas, setExistingDeltas] = useState<Record<string, number>>(
    {},
  );
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newInternalNotes, setNewInternalNotes] = useState('');
  const [newDeltas, setNewDeltas] = useState<Record<string, number>>({});

  const selectedDeliverable = deliverables.find(
    (deliverable) => deliverable.id === deliverableId,
  );
  const selectedServiceTypes =
    selectedDeliverable?.service_type_pools.map((pool) => ({
      id: getPoolServiceTypeId(pool),
      label: pool.service_type.label,
    })) ?? [];

  const deliverableOptions = [
    { value: '', label: 'Select deliverable' },
    ...deliverables.map((deliverable) => ({
      value: deliverable.id,
      label: deliverable.name,
    })),
  ];

  const addExisting = async () => {
    if (!deliverableId || !hasPositiveDelta(existingDeltas)) {
      return;
    }

    const ok = await onCreateItem({
      item_type: 'add_to_existing',
      deliverable_id: deliverableId,
      service_type_deltas: toPositiveDeltas(existingDeltas),
    });

    if (ok) {
      setDeliverableId('');
      setExistingDeltas({});
    }
  };

  const addNew = async () => {
    if (!newName.trim() || !hasPositiveDelta(newDeltas)) {
      return;
    }

    const ok = await onCreateItem({
      item_type: 'add_new_deliverable',
      payload: {
        name: newName.trim(),
        description: newDescription,
        internal_notes: newInternalNotes,
      },
      service_type_deltas: toPositiveDeltas(newDeltas),
    });

    if (ok) {
      setNewName('');
      setNewDescription('');
      setNewInternalNotes('');
      setNewDeltas({});
    }
  };

  return (
    <div className={styles.stepBody}>
      <StepHeading
        stepIndex={2}
        title="Define what changes"
        description="Add to existing deliverables or create brand new deliverables as separate change items."
      />
      {canEdit ? (
        <div className={styles.changeModeButtons}>
          <SecondaryButton
            ariaLabel="Add to existing deliverable"
            backgroundOverride={
              mode === 'add_to_existing' ? 'emphasis' : 'theme'
            }
            onClick={() => setMode('add_to_existing')}
          >
            Add to Existing Deliverable
          </SecondaryButton>
          <SecondaryButton
            ariaLabel="Add new deliverable"
            backgroundOverride={
              mode === 'add_new_deliverable' ? 'emphasis' : 'theme'
            }
            onClick={() => setMode('add_new_deliverable')}
          >
            Add New Deliverable
          </SecondaryButton>
        </div>
      ) : null}

      {mode === 'add_to_existing' ? (
        <div className={styles.changeItemCard}>
          <SmartInput
            type="select"
            name="change-order-existing-deliverable"
            label="Deliverable"
            value={deliverableId}
            options={deliverableOptions}
            onChange={(value) => {
              setDeliverableId(String(value));
              setExistingDeltas({});
            }}
          />
          <DeltaRows
            serviceTypes={selectedServiceTypes}
            availableServiceTypes={serviceTypes}
            jobServiceTypeIds={jobServiceTypeIds}
            deltas={existingDeltas}
            onChange={setExistingDeltas}
            onCreateServiceType={onCreateServiceType}
            disabled={!canEdit || !deliverableId}
          />
          <SecondaryButton
            ariaLabel="Add existing deliverable change"
            onClick={() => void addExisting()}
            disabled={
              !deliverableId ||
              !hasPositiveDelta(existingDeltas) ||
              isCreatingItem
            }
          >
            Add Change Item
          </SecondaryButton>
        </div>
      ) : null}

      {mode === 'add_new_deliverable' ? (
        <div className={styles.changeItemCard}>
          <SmartInput
            type="text"
            name="change-order-new-deliverable-name"
            label="Deliverable Name"
            value={newName}
            onChange={(value) => setNewName(String(value))}
          />
          <div className={styles.twoColumnGrid}>
            <SmartInput
              type="textarea"
              name="change-order-new-deliverable-description"
              label="Description"
              value={newDescription}
              onChange={(value) => setNewDescription(String(value))}
            />
            <SmartInput
              type="textarea"
              name="change-order-new-deliverable-details"
              label="Details"
              value={newInternalNotes}
              onChange={(value) => setNewInternalNotes(String(value))}
            />
          </div>
          <DeltaRows
            serviceTypes={jobServiceTypes}
            availableServiceTypes={serviceTypes}
            jobServiceTypeIds={jobServiceTypeIds}
            deltas={newDeltas}
            onChange={setNewDeltas}
            onCreateServiceType={onCreateServiceType}
            disabled={!canEdit}
          />
          <SecondaryButton
            ariaLabel="Add new deliverable change"
            onClick={() => void addNew()}
            disabled={
              !newName.trim() || !hasPositiveDelta(newDeltas) || isCreatingItem
            }
          >
            Add Change Item
          </SecondaryButton>
        </div>
      ) : null}

      <ChangeOrderItemsSummary
        items={changeOrder.items}
        deliverables={deliverables}
        serviceTypes={serviceTypes}
        canDelete={canEdit}
        isDeleting={isDeletingItem}
        onDeleteItem={onDeleteItem}
      />

      <ChangeOrderFooter
        impactHours={changeOrder.impact_hours}
        onCancel={onCancel}
        onBack={onBack}
        onContinue={onContinue}
        canContinue={changeOrder.items.length > 0}
      />
    </div>
  );
}

function ChangeOrderItemsSummary({
  items,
  deliverables,
  serviceTypes,
  canDelete,
  isDeleting,
  onDeleteItem,
}: {
  items: JobChangeOrderItem[];
  deliverables: JobDeliverable[];
  serviceTypes: ServiceTypeOption[];
  canDelete: boolean;
  isDeleting: boolean;
  onDeleteItem: (itemId: string) => Promise<void>;
}) {
  const deliverablesById = useMemo(
    () =>
      new Map(deliverables.map((deliverable) => [deliverable.id, deliverable])),
    [deliverables],
  );
  const serviceLabels = useMemo(
    () => new Map(serviceTypes.map((service) => [service.id, service.label])),
    [serviceTypes],
  );

  if (items.length === 0) {
    return (
      <p className={styles.detailIntro__muted}>
        0 change items in this change order.
      </p>
    );
  }

  return (
    <div className={styles.itemSummaryList}>
      {items.map((item) => (
        <div className={styles.itemSummary} key={item.id}>
          <div className={styles.itemSummary__row}>
            <span className={styles.itemSummary__title}>
              {getItemTitle(item, deliverablesById)}
            </span>
            {canDelete ? (
              <OptionsMenu
                ariaLabel="Change item actions"
                actions={[
                  {
                    key: 'delete',
                    label: 'Delete',
                    tone: 'danger',
                    loading: isDeleting,
                    onSelect: () => void onDeleteItem(item.id),
                  },
                ]}
              />
            ) : null}
          </div>
          {Object.entries(item.service_type_deltas).map(([id, hours]) => (
            <div className={styles.itemSummary__row} key={id}>
              <span>{serviceLabels.get(id) ?? id}</span>
              <span className={styles.itemSummary__impact}>
                {formatImpactHours(hours)}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function NetNetStep({
  changeOrder,
  deliverables,
  serviceTypes,
  onCancel,
  onBack,
  onContinue,
}: {
  changeOrder: JobChangeOrderDetail;
  deliverables: JobDeliverable[];
  serviceTypes: ServiceTypeOption[];
  onCancel: () => void;
  onBack: () => void;
  onContinue: () => void;
}) {
  return (
    <div className={styles.stepBody}>
      <StepHeading
        stepIndex={3}
        title="Net Net"
        description="Review the plan delta against the current plan before you apply it."
      />
      <ChangeOrderItemsSummary
        items={changeOrder.items}
        deliverables={deliverables}
        serviceTypes={serviceTypes}
        canDelete={false}
        isDeleting={false}
        onDeleteItem={async () => {}}
      />
      <div className={styles.badgeGrid}>
        <DataBadge
          title="Current Deliverables"
          value={deliverables.length}
          description="Before applying"
        />
        <DataBadge
          title="Plan Delta"
          value={formatImpactHours(changeOrder.impact_hours)}
          variant={changeOrder.impact_hours > 0 ? 'success' : 'default'}
        />
        <DataBadge
          title="Total Impact"
          value={formatImpactHours(changeOrder.impact_hours)}
          variant={changeOrder.impact_hours > 0 ? 'success' : 'default'}
        />
      </div>
      <ChangeOrderFooter
        impactHours={changeOrder.impact_hours}
        onCancel={onCancel}
        onBack={onBack}
        onContinue={onContinue}
      />
    </div>
  );
}

function ApplyStep({
  changeOrder,
  deliverables,
  serviceTypes,
  canApply,
  isApplying,
  onCancel,
  onBack,
  onApply,
}: {
  changeOrder: JobChangeOrderDetail;
  deliverables: JobDeliverable[];
  serviceTypes: ServiceTypeOption[];
  canApply: boolean;
  isApplying: boolean;
  onCancel: () => void;
  onBack: () => void;
  onApply: () => void;
}) {
  return (
    <div className={styles.stepBody}>
      <StepHeading
        stepIndex={4}
        title="Confirm and apply"
        description="Applying will visibly update the job scope and keep the change order auditable."
      />
      <ChangeOrderItemsSummary
        items={changeOrder.items}
        deliverables={deliverables}
        serviceTypes={serviceTypes}
        canDelete={false}
        isDeleting={false}
        onDeleteItem={async () => {}}
      />
      <div className={styles.badgeGrid}>
        <DataBadge title="Changes" value={changeOrder.items.length} />
        <DataBadge
          title="Impact"
          value={formatImpactHours(changeOrder.impact_hours)}
          variant={changeOrder.impact_hours > 0 ? 'success' : 'default'}
        />
        <DataBadge
          title="Status"
          value={changeOrder.status === 'applied' ? 'Applied' : 'Ready'}
        />
      </div>
      <ChangeOrderFooter
        impactHours={changeOrder.impact_hours}
        onCancel={onCancel}
        onBack={onBack}
        onContinue={onApply}
        canContinue={canApply && changeOrder.items.length > 0}
        continueLabel={
          changeOrder.status === 'applied' ? 'Applied' : 'Apply Change Order'
        }
        isSaving={isApplying}
      />
    </div>
  );
}

export function JobChangeOrderDetailView({
  jobId,
  changeOrderId,
  jobName,
  jobServiceTypeIds,
}: JobChangeOrderDetailViewProps) {
  const workspaceRoutes = useWorkspaceRoutes();
  const [activeStep, setActiveStep] = useState<StepId>('summary');
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);
  const [serviceTypeModalOpen, setServiceTypeModalOpen] = useState(false);
  const summaryDraftRef = useRef<{
    changeOrderId: string;
    draft: SummaryDraft;
  } | null>(null);
  const legacyCanEditChangeOrders = useCanWorkspaceAction(
    'job_management',
    'edit',
  );
  const canEdit = useCanScopedWorkspaceAction(
    'change_order_management',
    'edit',
    {
      entity_type: 'job',
      entity_id: jobId,
    },
    legacyCanEditChangeOrders,
  );

  const { data, isLoading, isError, error } = useGetJobChangeOrderQuery(
    changeOrderId || skipToken,
  );
  const { data: deliverablesData } = useGetJobDeliverablesQuery(
    jobId
      ? {
          jobId,
          params: { page_size: 100 },
        }
      : skipToken,
  );
  const { data: serviceTypesData, refetch: refetchServiceTypes } =
    useGetServiceTypesQuery({
      page_size: 100,
      show_archived: false,
    });
  const [patchChangeOrder, { isLoading: isSavingSummary }] =
    usePatchJobChangeOrderMutation();
  const [createItem, { isLoading: isCreatingItem }] =
    useCreateJobChangeOrderItemMutation();
  const [deleteItem, { isLoading: isDeletingItem }] =
    useDeleteJobChangeOrderItemMutation();
  const [applyChangeOrder, { isLoading: isApplying }] =
    useApplyJobChangeOrderMutation();

  const changeOrder = data?.data;
  const deliverables = useMemo(
    () => normalizeDeliverables(deliverablesData?.data),
    [deliverablesData?.data],
  );
  const serviceTypes = useMemo(
    () =>
      getServiceTypeOptions(
        serviceTypesData?.data?.results ?? [],
        deliverables,
      ),
    [deliverables, serviceTypesData?.data?.results],
  );
  const jobServiceTypes = useMemo(
    () =>
      getJobServiceTypeOptions(serviceTypes, jobServiceTypeIds, deliverables),
    [deliverables, jobServiceTypeIds, serviceTypes],
  );

  const backHref = workspaceRoutes.jobs.detailChangeOrders(jobId);
  const goBack = useContextTabBack(backHref);
  const stepIndex = Math.max(
    0,
    STEPS.findIndex((step) => step.id === activeStep),
  );
  const sections = STEPS.map((step, index) => ({
    ...step,
    completed: index < stepIndex || changeOrder?.status === 'applied',
  }));
  const canMutateDraft = canEdit.allowed && changeOrder?.status === 'draft';
  const loadedChangeOrderId = changeOrder?.id;
  const loadedChangeOrderName = changeOrder?.name;
  const loadedChangeOrderNotes = changeOrder?.notes;

  useEffect(() => {
    if (!loadedChangeOrderId || loadedChangeOrderName === undefined) {
      summaryDraftRef.current = null;
      return;
    }

    summaryDraftRef.current = {
      changeOrderId: loadedChangeOrderId,
      draft: {
        name: loadedChangeOrderName,
        notes: loadedChangeOrderNotes ?? '',
      },
    };
  }, [loadedChangeOrderId, loadedChangeOrderName, loadedChangeOrderNotes]);

  const saveSummary = async (payload: { name: string; notes: string }) => {
    if (!canMutateDraft || !changeOrder) {
      return true;
    }

    try {
      await patchChangeOrder({
        changeOrderId: changeOrder.id,
        payload,
      }).unwrap();
      return true;
    } catch (saveError) {
      notify({ type: 'error', message: getErrorMessage(saveError) });
      return false;
    }
  };

  const saveCurrentSummaryDraft = async () => {
    const draft = summaryDraftRef.current?.draft;

    if (!draft) {
      return true;
    }

    if (draft.name.trim().length === 0) {
      notify({
        type: 'error',
        message: 'Name this change order before continuing.',
      });
      return false;
    }

    return saveSummary({ name: draft.name.trim(), notes: draft.notes });
  };

  const handleStepChange = (nextStep: StepId) => {
    if (nextStep === activeStep) {
      return;
    }

    if (activeStep === 'summary' && nextStep !== 'summary') {
      void saveCurrentSummaryDraft().then((ok) => {
        if (ok) {
          setActiveStep(nextStep);
        }
      });
      return;
    }

    setActiveStep(nextStep);
  };

  const createChangeItem = async (payload: CreateJobChangeOrderItemPayload) => {
    if (!canMutateDraft || !changeOrder) {
      return false;
    }

    try {
      await createItem({
        changeOrderId: changeOrder.id,
        payload,
      }).unwrap();
      notify({ type: 'success', message: 'Change item added.' });
      return true;
    } catch (createError) {
      notify({ type: 'error', message: getErrorMessage(createError) });
      return false;
    }
  };

  const deleteChangeItem = async (itemId: string) => {
    if (!canMutateDraft) {
      return;
    }

    try {
      await deleteItem(itemId).unwrap();
      notify({ type: 'success', message: 'Change item deleted.' });
    } catch (deleteError) {
      notify({ type: 'error', message: getErrorMessage(deleteError) });
    }
  };

  const applyDraft = async () => {
    if (!canMutateDraft || !changeOrder) {
      return;
    }

    try {
      await applyChangeOrder({
        changeOrderId: changeOrder.id,
        jobId,
      }).unwrap();
      notify({ type: 'success', message: 'Change order applied.' });
      setApplyConfirmOpen(false);
      goBack();
    } catch (applyError) {
      notify({ type: 'error', message: getErrorMessage(applyError) });
    }
  };

  if (isLoading) {
    return (
      <div className={styles.detailView}>
        <Skeleton variant="rectangular" height={48} />
        <Skeleton variant="rectangular" height={420} />
      </div>
    );
  }

  if (isError || !changeOrder) {
    return (
      <EmptyState
        title="Change order unavailable"
        description={
          ((error as { data?: { message?: string } } | undefined)?.data
            ?.message as string | undefined) ||
          'Unable to load this change order right now.'
        }
      />
    );
  }

  return (
    <section className={styles.detailView}>
      <div className={styles.detailIntro}>
        <BackButton onClick={goBack} />
        <span className={styles.detailIntro__muted}>Jobs</span>
        <span className={styles.detailIntro__separator}>&bull;</span>
        <span className={styles.detailIntro__muted}>
          {jobName || 'Untitled Job'}
        </span>
        <span className={styles.detailIntro__separator}>&bull;</span>
        <h2 className={styles.detailIntro__title}>{changeOrder.name}</h2>
      </div>

      <div className={styles.wizard}>
        <SectionStepperTabs
          sections={sections}
          activeId={activeStep}
          onSectionChange={(id) => handleStepChange(id as StepId)}
          navigationMode="free"
          aria-label="Change order steps"
        />

        {activeStep === 'summary' ? (
          <SummaryStep
            key={changeOrder.id}
            changeOrder={changeOrder}
            canEdit={Boolean(canMutateDraft)}
            isSaving={isSavingSummary}
            onSave={saveSummary}
            onDraftChange={(draft) => {
              summaryDraftRef.current = {
                changeOrderId: changeOrder.id,
                draft,
              };
            }}
            onCancel={goBack}
            onContinue={() => setActiveStep('changes')}
          />
        ) : null}

        {activeStep === 'changes' ? (
          <ChangesStep
            changeOrder={changeOrder}
            deliverables={deliverables}
            serviceTypes={serviceTypes}
            jobServiceTypes={jobServiceTypes}
            jobServiceTypeIds={jobServiceTypeIds}
            canEdit={Boolean(canMutateDraft)}
            isCreatingItem={isCreatingItem}
            isDeletingItem={isDeletingItem}
            onCreateItem={createChangeItem}
            onDeleteItem={deleteChangeItem}
            onCreateServiceType={() => setServiceTypeModalOpen(true)}
            onCancel={goBack}
            onBack={() => setActiveStep('summary')}
            onContinue={() => setActiveStep('netnet')}
          />
        ) : null}

        {activeStep === 'netnet' ? (
          <NetNetStep
            changeOrder={changeOrder}
            deliverables={deliverables}
            serviceTypes={serviceTypes}
            onCancel={goBack}
            onBack={() => setActiveStep('changes')}
            onContinue={() => setActiveStep('apply')}
          />
        ) : null}

        {activeStep === 'apply' ? (
          <ApplyStep
            changeOrder={changeOrder}
            deliverables={deliverables}
            serviceTypes={serviceTypes}
            canApply={Boolean(canMutateDraft)}
            isApplying={isApplying}
            onCancel={goBack}
            onBack={() => setActiveStep('netnet')}
            onApply={() => setApplyConfirmOpen(true)}
          />
        ) : null}
      </div>

      <ConfirmActionModal
        open={applyConfirmOpen}
        title="Apply Change Order"
        description="This will update the job scope and cannot be undone without removing the change order."
        confirmText="APPLY"
        confirmButtonLabel="Apply Change Order"
        confirmButtonVariant="primary"
        isProcessing={isApplying}
        onConfirm={applyDraft}
        onClose={() => setApplyConfirmOpen(false)}
      />

      <Modal
        open={serviceTypeModalOpen}
        onClose={() => setServiceTypeModalOpen(false)}
        width="min(720px, 96vw)"
      >
        <div className="card-container-glass">
          <ServiceTypeModalForm
            mode="create"
            onCancel={() => setServiceTypeModalOpen(false)}
            onSuccess={() => {
              setServiceTypeModalOpen(false);
              void refetchServiceTypes();
            }}
          />
        </div>
      </Modal>
    </section>
  );
}
